import React, { useEffect, useMemo } from 'react';
import {
  Link,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { findPage, findSection, helpSections, HelpSection } from '../help/content';
import { renderHelpMarkdown } from '../help/renderMarkdown';
import './Help.css';

/**
 * /help — "Information and How-Tos" for everyone who meets QAIM: people with
 * a QAIM account (Users) and organisations whose applications sign in through
 * it (Tenants). Public by design: it never asks Kratos for a session, so it
 * reads the same signed in, signed out, or half-way through a sign-in.
 *
 * Pages are markdown under src/help/ (see help/content.ts), so adding one is
 * adding a file — the navigation below is built from them.
 */
export const Help: React.FC = () => (
  <Routes>
    <Route element={<HelpLayout />}>
      <Route index element={<HelpHome />} />
      <Route path=":section" element={<HelpSectionView />} />
      <Route path=":section/:page" element={<HelpArticle />} />
      <Route path="*" element={<HelpNotFound />} />
    </Route>
  </Routes>
);

function useHelpTitle(title: string) {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · QAIM Help`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

const HelpLayout: React.FC = () => (
  <div className="help">
    <nav className="help-nav" aria-label="Help topics">
      <NavLink to="/help" end className="help-nav-home">
        Information and How-Tos
      </NavLink>
      {helpSections.map((section) => (
        <div key={section.id} className="help-nav-section">
          <NavLink to={`/help/${section.id}`} end className="help-nav-heading">
            {section.title}
          </NavLink>
          <ul className="help-nav-list">
            {section.pages.map((page) => (
              <li key={page.slug}>
                <NavLink to={`/help/${section.id}/${page.slug}`} className="help-nav-link">
                  {page.title}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
    <div className="help-main">
      <Outlet />
    </div>
  </div>
);

const PageList: React.FC<{ section: HelpSection }> = ({ section }) => (
  <ul className="help-page-list">
    {section.pages.map((page) => (
      <li key={page.slug}>
        <Link to={`/help/${section.id}/${page.slug}`} className="help-page-tile">
          <span className="help-page-tile-title">{page.title}</span>
          {page.summary && <span className="help-page-tile-summary">{page.summary}</span>}
        </Link>
      </li>
    ))}
  </ul>
);

const HelpHome: React.FC = () => {
  useHelpTitle('Information and How-Tos');

  return (
    <article className="help-panel">
      <h1 className="help-title">Information and How-Tos</h1>
      <p className="help-lead">
        QAIM is a sign-in service: one account signs you in to every application that uses it.
        These pages explain how it works — for the people who sign in with it, and for the
        organisations whose applications rely on it.
      </p>

      {helpSections.map((section) => (
        <section
          key={section.id}
          className="help-section"
          aria-labelledby={`help-section-${section.id}`}
        >
          <h2 id={`help-section-${section.id}`} className="help-section-title">
            <Link to={`/help/${section.id}`}>{section.title}</Link>
          </h2>
          <p className="help-section-description">{section.description}</p>
          <PageList section={section} />
        </section>
      ))}
    </article>
  );
};

const HelpSectionView: React.FC = () => {
  const { section: sectionId } = useParams();
  const section = findSection(sectionId);
  useHelpTitle(section?.title ?? 'Page not found');

  if (!section) return <HelpNotFound />;

  return (
    <article className="help-panel">
      <Breadcrumbs trail={[]} />
      <h1 className="help-title">{section.title}</h1>
      <p className="help-lead">{section.description}</p>
      <PageList section={section} />
    </article>
  );
};

const HelpArticle: React.FC = () => {
  const { section: sectionId, page: slug } = useParams();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  const section = findSection(sectionId);
  const page = findPage(sectionId, slug);
  // The page's `#` title is rendered by the page chrome (above the contents
  // list), so the prose starts after it.
  const rendered = useMemo(
    () => (page ? renderHelpMarkdown(page.body.replace(/^#\s+.*(?:\n+|$)/, '')) : null),
    [page]
  );
  useHelpTitle(page?.title ?? 'Page not found');

  // A new page starts at its top — or at the #anchor it was linked to.
  useEffect(() => {
    if (hash) {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView?.();
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  if (!section || !page || !rendered) return <HelpNotFound />;

  // Links to other help pages stay inside the app (no reload); everything
  // else — /settings, /recovery, external sites — is a normal navigation.
  const followHelpLinks = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const link = (event.target as HTMLElement).closest('a');
    if (!link || link.target) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/^\/help(\/|$)/.test(url.pathname)) return;
    event.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  };

  const contents = rendered.headings.filter((heading) => heading.level === 2);
  const index = section.pages.indexOf(page);
  const previous = section.pages[index - 1];
  const next = section.pages[index + 1];

  return (
    <article className="help-panel">
      <Breadcrumbs trail={[{ to: `/help/${section.id}`, label: section.title }]} />
      <h1 className="help-title">{page.title}</h1>

      {contents.length > 1 && (
        <nav className="help-toc" aria-label="On this page">
          <p className="help-toc-title">On this page</p>
          <ul>
            {contents.map((heading) => (
              <li key={heading.id}>
                <a href={`#${heading.id}`}>{heading.text}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      {/* Sanitised in renderHelpMarkdown (DOMPurify) before it gets here. */}
      <div
        className="help-prose"
        onClick={followHelpLinks}
        dangerouslySetInnerHTML={{ __html: rendered.html }}
      />

      {(previous || next) && (
        <nav className="help-pager" aria-label={`More in ${section.title}`}>
          {previous ? (
            <Link to={`/help/${section.id}/${previous.slug}`} className="help-pager-link">
              <span className="help-pager-label">Previous</span>
              {previous.title}
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link
              to={`/help/${section.id}/${next.slug}`}
              className="help-pager-link help-pager-link--next"
            >
              <span className="help-pager-label">Next</span>
              {next.title}
            </Link>
          )}
        </nav>
      )}
    </article>
  );
};

const Breadcrumbs: React.FC<{ trail: Array<{ to: string; label: string }> }> = ({ trail }) => (
  <nav className="help-breadcrumbs" aria-label="Breadcrumb">
    <Link to="/help">Help</Link>
    {trail.map((crumb) => (
      <React.Fragment key={crumb.to}>
        <span aria-hidden="true"> / </span>
        <Link to={crumb.to}>{crumb.label}</Link>
      </React.Fragment>
    ))}
  </nav>
);

const HelpNotFound: React.FC = () => {
  useHelpTitle('Page not found');

  return (
    <article className="help-panel">
      <Breadcrumbs trail={[]} />
      <h1 className="help-title">Help page not found</h1>
      <p className="help-lead">
        There is no help page at this address. It may have moved — pick a topic from the list, or
        start again from the help home page.
      </p>
      <p>
        <Link to="/help" className="help-link">
          Go to Information and How-Tos
        </Link>
      </p>
    </article>
  );
};
